import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import CreatorFrame from '../../shared/CreatorFrame';
import { CampaignComposer } from '../../marketing-v2/features/email-composer';
import { analyseSms, analyseSmsExpanded, SMS_MERGE_TAGS } from '../../marketing-v2/features/sms-composer/smsUtils';
import { TRIGGERS, FILTER_CONDITIONS, applyStepDesign } from './journeysStore';
import NumberField from '../../common/NumberField';

/**
 * Journey builder: a vertical timeline (trigger -> steps -> goal), the SMB
 * pattern every comparable product converges on. Steps are inserted via
 * plus buttons between cards; no canvas, no free placement. The goal card
 * gives Klaviyo-style auto-exit ("end early when the client books") so most
 * journeys never need an explicit condition.
 */

const ink = '#1A1A1E';
const muted = '#8E8E93';
const line = '#ECECF0';

const generateId = () => `s-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

const STEP_META = {
  wait: { label: 'Wait', icon: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>
  ) },
  email: { label: 'Send email', icon: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m3 7 9 6 9-6" /></svg>
  ) },
  sms: { label: 'Send text', icon: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>
  ) },
  filter: { label: 'Only continue if', icon: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 4a1 1 0 0 1 1-1h16a1 1 0 0 1 1 1v2.6a1 1 0 0 1-.3.7l-6.4 6.4a1 1 0 0 0-.3.7V17l-4 4v-6.6a1 1 0 0 0-.3-.7L3.3 7.3A1 1 0 0 1 3 6.6V4z" /></svg>
  ) },
};

const cardStyle = {
  background: '#fff',
  border: `1px solid ${line}`,
  borderRadius: 14,
  padding: '16px 18px',
};

const Connector = () => (
  <div style={{ width: 2, height: 18, background: '#DEDEE2', margin: '0 auto' }} />
);

const AddStepButton = ({ onAdd }) => {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ position: 'relative', display: 'flex', justifyContent: 'center', padding: '2px 0' }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label="Add a step"
        style={{
          width: 26, height: 26, borderRadius: '50%', border: `1.5px dashed ${open ? '#007EA0' : '#C7C7CC'}`,
          background: '#fff', color: open ? '#007EA0' : muted, cursor: 'pointer',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s'
        }}
      >
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><path d="M12 5v14M5 12h14" /></svg>
      </button>
      {open && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 40 }} onClick={() => setOpen(false)} />
          <div style={{
            position: 'absolute', top: 32, left: '50%', transform: 'translateX(-50%)', zIndex: 41,
            background: '#fff', border: `1px solid ${line}`, borderRadius: 12,
            boxShadow: '0 12px 32px rgba(26,26,30,0.14)', padding: 6, width: 200
          }}>
            {Object.entries(STEP_META).map(([type, meta]) => (
              <button
                key={type}
                type="button"
                onClick={() => { setOpen(false); onAdd(type); }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '9px 11px',
                  border: 'none', background: 'transparent', borderRadius: 8, fontSize: 13,
                  fontWeight: 600, color: ink, cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left'
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = '#F2F2F7'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
              >
                <span style={{ color: '#007EA0', display: 'inline-flex' }}>{meta.icon}</span>
                {meta.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const StepHeader = ({ type, onDelete }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 10 }}>
    <span style={{
      width: 28, height: 28, borderRadius: 8, background: 'rgba(0,126,160,0.08)', color: '#007EA0',
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
    }}>
      {STEP_META[type].icon}
    </span>
    <span style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: muted, flex: 1 }}>
      {STEP_META[type].label}
    </span>
    <button
      type="button"
      onClick={onDelete}
      aria-label="Remove step"
      style={{ border: 'none', background: 'none', color: '#C7C7CC', cursor: 'pointer', padding: 4, display: 'inline-flex' }}
      onMouseEnter={(e) => { e.currentTarget.style.color = '#B14438'; }}
      onMouseLeave={(e) => { e.currentTarget.style.color = '#C7C7CC'; }}
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18" /><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /></svg>
    </button>
  </div>
);

const JourneyBuilder = ({ initialJourney, onSave, onClose }) => {
  const [journey, setJourney] = useState(() => ({
    name: '',
    status: 'paused',
    trigger: { type: 'new-client' },
    goalExit: true,
    steps: [],
    ...(initialJourney || {}),
  }));
  const [designingStepId, setDesigningStepId] = useState(null);

  const setStep = (stepId, updates) => {
    setJourney((j) => ({ ...j, steps: j.steps.map((s) => (s.id === stepId ? { ...s, ...updates } : s)) }));
  };

  const removeStep = (stepId) => {
    setJourney((j) => ({ ...j, steps: j.steps.filter((s) => s.id !== stepId) }));
  };

  const addStepAt = (index, type) => {
    const base = { id: generateId(), type };
    const step =
      type === 'wait' ? { ...base, days: 7 } :
      type === 'email' ? { ...base, subject: '', blocks: [] } :
      type === 'sms' ? { ...base, message: '' } :
      { ...base, condition: 'no-future-booking' };
    setJourney((j) => {
      const steps = [...j.steps];
      steps.splice(index, 0, step);
      return { ...j, steps };
    });
  };

  const designingStep = journey.steps.find((s) => s.id === designingStepId) || null;

  const canSave = journey.steps.some((s) => s.type === 'email' || s.type === 'sms');

  const renderStepBody = (step) => {
    if (step.type === 'wait') {
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 14, color: ink }}>Wait</span>
          <NumberField
            min={1}
            max={365}
            fallback={1}
            value={step.days}
            onChange={(days) => setStep(step.id, { days })}
            style={{ width: 64, height: 38, padding: '0 10px', border: `1px solid ${line}`, borderRadius: 9, fontSize: 14, fontWeight: 600, textAlign: 'center', fontFamily: 'inherit', outline: 'none' }}
          />
          <span style={{ fontSize: 14, color: ink }}>{step.days === 1 ? 'day' : 'days'}</span>
          <span style={{ fontSize: 12, color: muted, marginLeft: 'auto' }}>then continue</span>
        </div>
      );
    }
    if (step.type === 'email') {
      const hasDesign = Array.isArray(step.blocks) && step.blocks.length > 0;
      return (
        <div>
          <input
            type="text"
            value={step.subject}
            onChange={(e) => setStep(step.id, { subject: e.target.value })}
            placeholder="Subject line"
            style={{ width: '100%', height: 40, padding: '0 12px', boxSizing: 'border-box', border: `1px solid ${line}`, borderRadius: 9, fontSize: 13.5, fontFamily: 'inherit', outline: 'none', marginBottom: 10 }}
          />
          <button
            type="button"
            onClick={() => setDesigningStepId(step.id)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 7, padding: '9px 14px',
              border: hasDesign ? `1px solid ${line}` : '1px solid rgba(0,126,160,0.4)',
              borderRadius: 9, background: hasDesign ? '#fff' : 'rgba(0,126,160,0.05)',
              fontSize: 12.5, fontWeight: 700, color: '#007EA0', cursor: 'pointer', fontFamily: 'inherit'
            }}
          >
            {hasDesign ? `Edit design (${step.blocks.length} blocks)` : 'Design this email'}
          </button>
        </div>
      );
    }
    if (step.type === 'sms') {
      const sms = analyseSms(step.message);
      const est = analyseSmsExpanded(step.message);
      return (
        <div>
          <textarea
            value={step.message}
            onChange={(e) => setStep(step.id, { message: e.target.value })}
            placeholder="Hi {{FirstName}}, ..."
            style={{ width: '100%', minHeight: 76, padding: '10px 12px', boxSizing: 'border-box', border: `1px solid ${sms.overLimit ? '#B14438' : line}`, borderRadius: 9, fontSize: 13.5, lineHeight: 1.5, fontFamily: 'inherit', outline: 'none', resize: 'vertical' }}
          />
          {est.segments > 1 && (
            <div style={{ marginTop: 7, background: '#FFF8E6', border: '1px solid #F5D982', borderRadius: 8, padding: '7px 10px', fontSize: 12, color: '#7A5B00', lineHeight: 1.5 }}>
              Once names and links are filled in, this message will be longer than one text
              ({est.single} characters) — each client will be charged for <strong>{est.segments} texts</strong> instead of one.
              Trim it under the limit to pay for one.
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginTop: 7, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 11.5, fontWeight: 600, color: sms.overLimit ? '#B14438' : muted }}>
              {sms.length} characters · ≈{est.segments} {est.segments === 1 ? 'text' : 'texts'} per client after personalisation
            </span>
            <span style={{ display: 'inline-flex', gap: 6 }}>
              {SMS_MERGE_TAGS.map(({ tag }) => (
                <button
                  key={tag}
                  type="button"
                  onClick={() => setStep(step.id, { message: `${step.message || ''}${step.message ? ' ' : ''}${tag}` })}
                  style={{ padding: '4px 9px', borderRadius: 100, border: `1px solid ${line}`, background: '#fff', fontSize: 10.5, fontWeight: 600, color: '#4B4B52', cursor: 'pointer', fontFamily: 'inherit' }}
                >
                  {tag}
                </button>
              ))}
            </span>
          </div>
        </div>
      );
    }
    // filter
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 14, color: ink }}>Only continue if</span>
        <select
          value={step.condition}
          onChange={(e) => setStep(step.id, { condition: e.target.value })}
          style={{ height: 38, padding: '0 10px', border: `1px solid ${line}`, borderRadius: 9, fontSize: 13.5, fontFamily: 'inherit', outline: 'none', background: '#fff' }}
        >
          {FILTER_CONDITIONS.map((c) => (
            <option key={c.id} value={c.id}>{c.label}</option>
          ))}
        </select>
      </div>
    );
  };

  return (
    <CreatorFrame
      name={journey.name}
      onNameChange={(name) => setJourney((j) => ({ ...j, name }))}
      namePlaceholder="Untitled journey"
      onClose={onClose}
      primaryAction={{
        label: 'Save journey',
        onClick: () => onSave({ ...journey, name: journey.name || 'Untitled journey' }),
        disabled: !canSave,
      }}
    >
      <div style={{ minHeight: '100%', backgroundColor: '#fafafa', fontFamily: 'inherit' }}>
        <div style={{ maxWidth: 620, margin: '0 auto', padding: '36px clamp(16px, 5vw, 40px) 120px' }}>

          {/* Trigger */}
          <div style={{ ...cardStyle, borderColor: '#29282D', borderWidth: 1.5 }}>
            <p style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: muted, margin: '0 0 10px' }}>
              Starts when
            </p>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <select
                value={journey.trigger.type}
                onChange={(e) => { const v = e.target.value; setJourney((j) => ({ ...j, trigger: { type: v, ...(v === 'lapsed' ? { lapsedWeeks: j.trigger.lapsedWeeks || 8 } : {}) } })); }}
                style={{ height: 40, padding: '0 12px', border: `1px solid ${line}`, borderRadius: 9, fontSize: 14, fontWeight: 600, fontFamily: 'inherit', outline: 'none', background: '#fff' }}
              >
                {TRIGGERS.map((t) => (
                  <option key={t.id} value={t.id}>{t.label}</option>
                ))}
              </select>
              {journey.trigger.type === 'lapsed' && (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 14, color: ink }}>
                  for
                  <NumberField
                    min={2}
                    max={52}
                    fallback={8}
                    value={journey.trigger.lapsedWeeks || 8}
                    onChange={(lapsedWeeks) => setJourney((j) => ({ ...j, trigger: { ...j.trigger, lapsedWeeks } }))}
                    style={{ width: 58, height: 38, padding: '0 8px', border: `1px solid ${line}`, borderRadius: 9, fontSize: 14, fontWeight: 600, textAlign: 'center', fontFamily: 'inherit', outline: 'none' }}
                  />
                  weeks
                </span>
              )}
            </div>
            <p style={{ fontSize: 12, color: muted, margin: '10px 0 0' }}>
              {TRIGGERS.find((t) => t.id === journey.trigger.type)?.description}
              {'. '}Each client enters this journey once.
            </p>
          </div>

          <Connector />
          <AddStepButton onAdd={(type) => addStepAt(0, type)} />
          <Connector />

          {/* Steps */}
          {journey.steps.map((step, index) => (
            <React.Fragment key={step.id}>
              <div style={cardStyle}>
                <StepHeader type={step.type} onDelete={() => removeStep(step.id)} />
                {renderStepBody(step)}
              </div>
              <Connector />
              <AddStepButton onAdd={(type) => addStepAt(index + 1, type)} />
              <Connector />
            </React.Fragment>
          ))}

          {journey.steps.length === 0 && (
            <div style={{ textAlign: 'center', padding: '8px 0 14px' }}>
              <p style={{ fontSize: 13, color: muted, margin: 0 }}>Add your first step with the plus above.</p>
            </div>
          )}

          {/* Goal */}
          <div style={{
            ...cardStyle,
            borderColor: journey.goalExit ? 'rgba(0,126,160,0.45)' : line,
            background: journey.goalExit ? 'rgba(0,126,160,0.04)' : '#fff'
          }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
              <span style={{
                width: 28, height: 28, borderRadius: 8, flexShrink: 0,
                background: journey.goalExit ? '#007EA0' : '#F2F2F7', color: journey.goalExit ? '#fff' : muted,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center'
              }}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
              </span>
              <div style={{ flex: 1 }}>
                <p style={{ fontSize: 14, fontWeight: 700, color: ink, margin: '0 0 3px' }}>Goal: the client books</p>
                <p style={{ fontSize: 12.5, color: muted, lineHeight: 1.5, margin: 0 }}>
                  {journey.goalExit
                    ? 'When a client books, they leave this journey before the next message. No one gets nudged about something they have already done.'
                    : 'Clients receive every step even if they book along the way.'}
                </p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={journey.goalExit}
                onClick={() => setJourney((j) => ({ ...j, goalExit: !j.goalExit }))}
                style={{
                  width: 44, height: 26, borderRadius: 100, border: 'none', cursor: 'pointer', padding: 2,
                  background: journey.goalExit ? '#007EA0' : '#DEDEE2', transition: 'background 0.15s', flexShrink: 0
                }}
              >
                <span style={{
                  display: 'block', width: 22, height: 22, borderRadius: '50%', background: '#fff',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.25)', transition: 'transform 0.15s',
                  transform: journey.goalExit ? 'translateX(18px)' : 'translateX(0)'
                }} />
              </button>
            </div>
          </div>

          {/* Built-in guardrails */}
          <p style={{ fontSize: 11.5, color: muted, lineHeight: 1.6, margin: '18px 4px 0', textAlign: 'center' }}>
            Built in: messages only go to opted-in clients, texts wait for daytime hours, and anyone who unsubscribes leaves the journey immediately.
          </p>
        </div>
      </div>

      {/* Email step designer */}
      {designingStep && ReactDOM.createPortal(
        <div style={{ position: 'fixed', inset: 0, zIndex: 100015 }}>
          <CampaignComposer
            mode="design"
            initialBlocks={designingStep.blocks && designingStep.blocks.length ? designingStep.blocks : undefined}
            initialSubject={designingStep.subject}
            onBack={() => setDesigningStepId(null)}
            onSaveDesign={(design) => {
              const id = designingStep.id;
              setJourney((j) => ({ ...j, steps: j.steps.map((s) => (s.id === id ? applyStepDesign(s, design) : s)) }));
              setDesigningStepId(null);
            }}
          />
        </div>,
        document.body
      )}
    </CreatorFrame>
  );
};

export default JourneyBuilder;
